import { useState } from 'react';
import styled, { css } from 'styled-components';
import { SearchDropdown, SelectDropdown } from '@prosolve/common-components-ui';

const getValueByPath = (obj, path) => {
  if (!obj || !path) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(obj, path)) {
    return obj[path];
  }

  return path.split('.').reduce((value, key) => value?.[key], obj);
}

const valueToStrings = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap(valueToStrings);
  }

  if (value && typeof value === 'object') {
    return valueToStrings(value.Name || value.name || value.label);
  }

  if (value === undefined || value === null) {
    return [];
  }

  return String(value)
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);
}

const getImplementationValues = (implementation, fields = []) => {
  return fields.flatMap((field) => valueToStrings(getValueByPath(implementation, field)));
}

export const implementationCalendarFieldConfig = [
  {
    key: 'account',
    label: 'Account',
    allLabel: 'All accounts',
    fields: ['accountName', 'Account__r.Name'],
    control: 'search',
    width: '240px'
  },
  {
    key: 'ess',
    label: 'ESS',
    allLabel: 'All ESS',
    fields: ['ess', 'essName', 'essLeader', 'essLeaderName', 'ESS_Leader__r.Name'],
    control: 'search',
    width: '180px'
  },
  {
    key: 'im',
    label: 'IM',
    allLabel: 'All IM',
    fields: ['im', 'imName', 'implementationManager', 'implementationManagerName', 'projectManager', 'projectManagerName', 'Project_Manager__r.Name', 'Implementation_Manager__r.Name'],
    control: 'search',
    width: '180px'
  },
  {
    key: 'product',
    label: 'Product Selections',
    allLabel: 'All products',
    fields: ['productSelection', 'productSelections', 'whichProducts', 'product', 'Product_Selection__c', 'Which_products__c', 'Parent_Curiculum_Product__c'],
    control: 'search',
    width: '210px'
  },
  {
    key: 'schoolDaySummer',
    label: 'School day vs summer',
    allLabel: 'All types',
    fields: ['schoolDayVsSummer', 'whereIsItUsed', 'implementationWhereIsItUsed', 'Where_is_it_used__c'],
    width: '190px',
    getValues: (implementation) => {
      const values = getImplementationValues(implementation, ['schoolDayVsSummer', 'whereIsItUsed', 'implementationWhereIsItUsed', 'Where_is_it_used__c']);

      if (values.length > 0) {
        return values;
      }

      const productValues = getImplementationValues(implementation, ['productSelection', 'productSelections', 'whichProducts', 'product', 'Product_Selection__c', 'Which_products__c', 'Parent_Curiculum_Product__c']);
      const productText = productValues.join(' ').toLowerCase();

      if (productText.includes('school day')) {
        return ['School Day'];
      }

      if (productText.includes('summer')) {
        return ['Summer'];
      }

      return [];
    }
  },
  {
    key: 'trainers',
    label: 'Trainers',
    allLabel: 'All trainers',
    fields: ['trainers', 'trainerNames', 'Trainer_Names__c'],
    control: 'search',
    width: '220px',
    getValues: (implementation) => {
      return getImplementationValues(implementation, ['trainers', 'trainerNames', 'Trainer_Names__c'])
        .flatMap((value) => value.split(','))
        .map((value) => value.trim())
        .filter(Boolean);
    }
  }
];

const FiltersWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;

  ${({ $isMobileView }) => $isMobileView && css`
    width: 100%;
  `}
`

const FilterField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: ${({ $width }) => $width || '155px'};
  color: #4b5563;
  font-size: 12px;

  ${({ $isMobileView }) => $isMobileView && css`
    width: 100%;
  `}
`

const getFilterValues = (implementation, filterConfig) => {
  if (filterConfig.getValues) {
    return filterConfig.getValues(implementation);
  }

  return getImplementationValues(implementation, filterConfig.fields);
}

export const getImplementationFieldValues = getFilterValues;

const getFilterOptions = (implementations = []) => {
  return implementationCalendarFieldConfig.reduce((result, filterConfig) => {
    const values = implementations.flatMap((implementation) => getFilterValues(implementation, filterConfig));
    const uniqueValues = [...new Set(values)].sort((a, b) => a.localeCompare(b));

    result[filterConfig.key] = uniqueValues.map((value) => ({
      value,
      text: value
    }));

    return result;
  }, {});
}

export const filterImplementations = (implementations = [], filters = {}) => {
  return implementations.filter((implementation) => {
    return implementationCalendarFieldConfig.every((filterConfig) => {
      const selectedValue = filters[filterConfig.key];

      if (!selectedValue) {
        return true;
      }

      return getFilterValues(implementation, filterConfig).includes(selectedValue);
    });
  });
}

export default function ImplementationCalendarFilters({
  filters = {},
  implementations = [],
  hiddenFilterKeys = [],
  isMobileView,
  onChange
}) {
  const [searchValues, setSearchValues] = useState({});

  const filterOptions = getFilterOptions(implementations);
  const hiddenFilterKeyMap = hiddenFilterKeys.reduce((result, key) => {
    result[key] = true;
    return result;
  }, {});
  const visibleFieldConfig = implementationCalendarFieldConfig.filter(({ key }) => !hiddenFilterKeyMap[key]);

  const handleFilterChange = (key, value) => {
    onChange({
      ...filters,
      [key]: value
    });
  }

  const getOptions = ({ key, allLabel }) => {
    return [
      { value: '', text: allLabel },
      ...(filterOptions[key] || [])
    ];
  }

  const getSearchOptions = (filterConfig) => {
    const search = (searchValues[filterConfig.key] || '').toLowerCase();
    const options = getOptions(filterConfig);

    if (!search) {
      return options;
    }

    return options.filter(({ value, text }) => !value || text.toLowerCase().includes(search));
  }

  return (
    <FiltersWrapper $isMobileView={isMobileView}>
      {visibleFieldConfig.map((filterConfig) => {
        const { key, label, width, control } = filterConfig;
        const options = filterOptions[key] || [];
        const dropdownWidth = isMobileView ? '100%' : width;

        return (
          <FilterField key={key} $width={dropdownWidth} $isMobileView={isMobileView}>
            <span>{label}</span>
            {control === 'search' ?
              <SearchDropdown
                value={filters[key] || ''}
                options={getSearchOptions(filterConfig)}
                onChange={(value) => handleFilterChange(key, value)}
                onSearch={(searchValue) => setSearchValues((currentSearchValues) => ({
                  ...currentSearchValues,
                  [key]: searchValue
                }))}
                disabled={options.length === 0}
                placeholder={filterConfig.allLabel}
                maxHeight='250px'
                width={dropdownWidth}
                isMobileView={isMobileView}
              />
            :
              <SelectDropdown
                value={filters[key] || ''}
                options={getOptions(filterConfig)}
                onChange={(value) => handleFilterChange(key, value)}
                disabled={options.length === 0}
                width={dropdownWidth}
                isMobileView={isMobileView}
              />
            }
          </FilterField>
        );
      })}
    </FiltersWrapper>
  );
}
